import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Linkedin, ArrowUp, Mail, Send } from 'lucide-react';
import { companyInfo, navLinks } from '../../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) e.name = 'Name required';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Valid email required';
    if (!formData.subject.trim() || formData.subject.trim().length < 4) e.subject = 'Subject required';
    if (!formData.message.trim() || formData.message.trim().length < 10) e.message = 'Message required (10+ chars)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const scrollTo = (href) => { const el = document.querySelector(href); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <footer id="contact" className="relative z-10 border-t border-[rgba(255,255,255,0.06)]" style={{ background: 'rgba(5,5,16,0.92)', backdropFilter: 'blur(20px)' }}>
      {/* Top gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#00D4FF]/40 to-transparent" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-16 md:py-20">
        {/* Contact Form Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16 pb-16 border-b border-[rgba(255,255,255,0.06)]">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="text-3xl md:text-4xl font-bold font-space text-[#F0F0F5] mb-3">Let's Work Together</h3>
            <p className="text-[#8A8AA3] text-base mb-8">Have a project in mind? Send us a message.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input name="name" value={formData.name} onChange={handleChange} placeholder="Your name"
                    className={`w-full bg-[rgba(255,255,255,0.03)] border ${errors.name ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.08)]'} rounded-xl px-4 py-3 text-[#F0F0F5] text-sm placeholder:text-[#4A4A65] focus:outline-none focus:border-[#00D4FF]/40 transition-colors`}
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email address"
                    className={`w-full bg-[rgba(255,255,255,0.03)] border ${errors.email ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.08)]'} rounded-xl px-4 py-3 text-[#F0F0F5] text-sm placeholder:text-[#4A4A65] focus:outline-none focus:border-[#00D4FF]/40 transition-colors`}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>
              <input name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject"
                className={`w-full bg-[rgba(255,255,255,0.03)] border ${errors.subject ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.08)]'} rounded-xl px-4 py-3 text-[#F0F0F5] text-sm placeholder:text-[#4A4A65] focus:outline-none focus:border-[#00D4FF]/40 transition-colors`}
              />
              {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}
              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={4}
                className={`w-full bg-[rgba(255,255,255,0.03)] border ${errors.message ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.08)]'} rounded-xl px-4 py-3 text-[#F0F0F5] text-sm placeholder:text-[#4A4A65] focus:outline-none focus:border-[#00D4FF]/40 transition-colors resize-none`}
              />
              {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
              <button type="submit" disabled={isSubmitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] text-white font-medium px-8 py-3.5 rounded-xl shadow-[0_0_20px_rgba(0,212,255,0.25)] hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 text-sm disabled:opacity-50">
                {isSubmitting ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
                  : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
              {submitStatus === 'success' && <p className="text-[#00FF88] text-sm mt-2">Message sent! We'll reply within 24 hours.</p>}
              {submitStatus === 'error' && <p className="text-red-400 text-sm mt-2">Something went wrong. Please try again.</p>}
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="space-y-8">
            <div>
              <h4 className="text-sm font-semibold text-[#F0F0F5] font-space mb-4">Contact</h4>
              <div className="space-y-3">
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-[#8A8AA3] hover:text-[#00D4FF] transition-colors text-sm">
                  <Mail className="w-4 h-4 text-[#00D4FF]" /> {companyInfo.email}
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[#F0F0F5] font-space mb-4">Socials</h4>
              <div className="flex gap-3">
                {[
                  { icon: Linkedin, href: companyInfo.social.linkedin, label: 'LinkedIn' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] text-[#4A4A65] hover:text-[#00D4FF] hover:border-[rgba(0,212,255,0.2)] transition-all">
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer links + copyright */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-[rgba(0,212,255,0.1)]">
          <div className="flex items-center gap-6">
            <img src={companyInfo.logo} alt="ABCI" className="h-14 sm:h-18 md:h-52 object-contain" />
            <div className="hidden md:flex gap-4">
              {navLinks.map(l => (
                <button key={l.href} onClick={() => scrollTo(l.href)} className="text-white hover:text-[#00D4FF] text-sm transition-colors">{l.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-white text-xs">&copy; {new Date().getFullYear()} ABCI. All rights reserved.</p>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-8 h-8 flex items-center justify-center rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)] text-[#4A4A65] hover:text-[#00D4FF] transition-all" aria-label="Back to top">
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
